/**
 * Website prices (billing price changes, four-eyes): the effective catalogue per SKU (charge prices in every currency,
 * partner-only wholesale, contact-sales), a propose form, and the change list with role- and ownership-aware actions.
 */
import { useState } from 'react';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { CurrencySchema, type Currency, type PriceChangeView } from '@qm/contracts/billing';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, DataTable, Field, Loadable, Money, Notice, Screen, When, type Tone } from '../kit/ui';
import type { PriceSkuView } from '../api';
import { canProposePrice, priceChangeActions, toMinor } from '../lib/cms-logic';
import { useAdminApi } from './layout';
import { CmsError } from './cms-shared';

const CURRENCIES = CurrencySchema.options;
export const CHANGE_TONE: Record<PriceChangeView['status'], Tone> = { proposed: 'warn', approved: 'good', rejected: 'bad', withdrawn: 'neutral' };

export function CmsPricesPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const data = useResource('cms:prices', api.prices);
  const me = useResource('admin.me', api.me);
  if (!canProposePrice(roles)) return <Screen title={t('portal.admin.cms.prices.title')}><Notice testId="prices-no-access">{t('portal.admin.cms.prices.noAccess')}</Notice></Screen>;
  const userId = me.status === 'ready' ? me.data.userId : null;
  return (
    <Screen title={t('portal.admin.cms.prices.title')} subtitle={t('portal.admin.cms.prices.subtitle')} testId="cms-prices">
      <Loadable resource={data}>
        {(d) => (
          <>
            <PriceTable items={d.items} />
            <ProposeForm items={d.items} onProposed={data.reload} />
            <ChangesList changes={d.changes} userId={userId} onChanged={data.reload} />
          </>
        )}
      </Loadable>
    </Screen>
  );
}

function PriceTable({ items }: { items: PriceSkuView[] }) {
  const { t } = useSurface();
  return (
    <Panel data-testid="price-table">
      <Heading level={2}>{t('portal.admin.cms.prices.current')}</Heading>
      <DataTable rows={items} rowKey={(i) => i.sku} empty={t('portal.kit.empty')} caption={t('portal.admin.cms.prices.current')} columns={[
        { key: 'sku', header: 'SKU', render: (i) => <code>{i.sku}</code> },
        { key: 'n', header: t('portal.admin.billing.product'), render: (i) => i.name },
        { key: 'p', header: t('portal.admin.cms.prices.charge'), render: (i) => (i.contactSales
          ? <Badge testId={`contact-sales-${i.sku}`}>{t('portal.admin.cms.prices.contactSales')}</Badge>
          : <span className="qx-toolbar">{i.prices.map((p) => <Money key={p.currency} minor={p.amountMinor} currency={p.currency} />)}{i.interval ? <Text tone="muted">{t(`portal.admin.cms.prices.per.${i.interval}`)}</Text> : null}</span>) },
        { key: 'w', header: t('portal.admin.cms.prices.wholesale'), render: (i) => (i.wholesale
          ? <span className="qx-toolbar" data-testid={`wholesale-${i.sku}`}><Money minor={i.wholesale.amountMinor} currency={i.wholesale.currency} /><Badge tone="warn">{t('portal.admin.cms.prices.partnerOnly')}</Badge></span>
          : '-') },
        { key: 's', header: t('portal.admin.billing.sellable'), narrow: 'hide', render: (i) => (i.sellable ? <Badge tone="good">{t('portal.admin.yes')}</Badge> : <Badge tone="warn">{t('portal.admin.billing.awaitingApproval')}</Badge>) },
      ]} />
    </Panel>
  );
}

export function ProposeForm({ items, onProposed }: { items: PriceSkuView[]; onProposed: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const priced = items.filter((i) => !i.contactSales);
  const [sku, setSku] = useState(priced[0]?.sku ?? '');
  const [kind, setKind] = useState<'price' | 'wholesale'>('price');
  const [currency, setCurrency] = useState<Currency>('AED');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const propose = useAction(api.proposePrice);
  const minor = toMinor(amount, currency);
  const valid = !!sku && minor !== null && minor > 0 && reason.trim().length >= 5;
  return (
    <Panel data-testid="propose-price">
      <Heading level={2}>{t('portal.admin.cms.prices.propose')}</Heading>
      <Text tone="muted">{t('portal.admin.cms.prices.proposeNote')}</Text>
      <form className="qx-toolbar" onSubmit={async (e) => {
        e.preventDefault();
        if (!valid) return;
        if (await propose.run({ sku, kind, currency, amountMinor: minor!, reason: reason.trim() })) { setAmount(''); setReason(''); onProposed(); }
      }}>
        <Field label="SKU">{(id) => <select id={id} name="sku" value={sku} onChange={(e) => setSku(e.target.value)}>{priced.map((i) => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}</select>}</Field>
        <Field label={t('portal.admin.cms.prices.kind')}>
          {(id) => (
            <select id={id} name="kind" value={kind} onChange={(e) => setKind(e.target.value as 'price' | 'wholesale')}>
              <option value="price">{t('portal.admin.cms.prices.kindPrice')}</option>
              <option value="wholesale">{t('portal.admin.cms.prices.kindWholesale')}</option>
            </select>
          )}
        </Field>
        <Field label={t('portal.admin.cms.prices.currency')}>{(id) => <select id={id} name="currency" value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>}</Field>
        <Field label={t('portal.admin.cms.prices.amount')} hint={minor !== null && minor > 0 ? t('portal.admin.cms.prices.minorHint', { minor }) : t('portal.admin.cms.prices.amountHint')}
          error={amount && (minor === null || minor <= 0) ? t('portal.admin.cms.prices.amountInvalid') : null}>
          {(id) => <input id={id} name="amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />}
        </Field>
        <Field label={t('portal.kit.reason')} hint={t('portal.kit.reasonHint', { n: 5 })}>{(id) => <textarea id={id} name="reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" disabled={!valid || propose.pending} data-testid="propose-submit">{t('portal.admin.cms.prices.proposeSubmit')}</Button>
      </form>
      <CmsError error={propose.error} />
    </Panel>
  );
}

export function ChangesList({ changes, userId, onChanged }: { changes: PriceChangeView[]; userId: string | null; onChanged: () => void }) {
  const { t } = useSurface();
  return (
    <Panel data-testid="price-changes">
      <Heading level={2}>{t('portal.admin.cms.prices.changes')}</Heading>
      <DataTable rows={changes} rowKey={(c) => c.id} empty={t('portal.admin.cms.prices.noChanges')} columns={[
        { key: 'sku', header: 'SKU', render: (c) => <code>{c.sku}</code> },
        { key: 'k', header: t('portal.admin.cms.prices.kind'), render: (c) => t(c.kind === 'price' ? 'portal.admin.cms.prices.kindPrice' : 'portal.admin.cms.prices.kindWholesale') },
        { key: 'a', header: t('portal.admin.cms.prices.amount'), render: (c) => <Money minor={c.amountMinor} currency={c.currency} /> },
        { key: 'r', header: t('portal.kit.reason'), render: (c) => c.reason },
        { key: 's', header: t('portal.admin.status'), render: (c) => <Badge tone={CHANGE_TONE[c.status]} testId={`change-${c.id}-${c.status}`}>{t(`portal.admin.cms.prices.status.${c.status}`)}</Badge> },
        { key: 'p', header: t('portal.admin.cms.prices.proposedBy'), narrow: 'hide', render: (c) => <span>{c.proposedBy === userId ? t('portal.admin.cms.prices.you') : c.proposedBy} · <When iso={c.proposedAt} /></span> },
        { key: 'd', header: t('portal.admin.cms.prices.decision'), narrow: 'hide', render: (c) => (c.decidedAt ? <span>{c.decisionNote ?? ''} {c.stripePriceId ? <code>{c.stripePriceId}</code> : null} · <When iso={c.decidedAt} /></span> : '-') },
        { key: 'x', header: t('portal.admin.cms.history.actions'), render: (c) => <ChangeActions change={c} userId={userId} onChanged={onChanged} /> },
      ]} />
    </Panel>
  );
}

export function ChangeActions({ change, userId, onChanged }: { change: PriceChangeView; userId: string | null; onChanged: () => void }) {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const can = priceChangeActions(change, roles, userId);
  const [mode, setMode] = useState<'approve' | 'reject' | null>(null);
  const [stripe, setStripe] = useState('');
  const [note, setNote] = useState('');
  const approve = useAction(() => api.approvePrice(change.id, { stripePriceId: can.needsStripeId ? stripe : undefined, note }));
  const reject = useAction(() => api.rejectPrice(change.id, note.trim()));
  const withdraw = useAction(() => api.withdrawPrice(change.id));
  const done = async (p: Promise<unknown>) => { if (await p) { setMode(null); onChanged(); } };
  if (!can.approve && !can.reject && !can.withdraw) {
    return change.status === 'proposed' && can.own && roles.includes('staff_finance') ? <Text tone="muted" data-testid={`own-${change.id}`}>{t('portal.admin.cms.prices.ownNote')}</Text> : null;
  }
  const stripeOk = !can.needsStripeId || /^price_[A-Za-z0-9_]+$/.test(stripe.trim());
  return (
    <div className="qx-toolbar" data-testid={`actions-${change.id}`}>
      {can.approve ? <Button variant="primary" onClick={() => setMode('approve')} data-testid={`approve-${change.id}`}>{t('portal.admin.cms.prices.approve')}</Button> : null}
      {can.reject ? <Button onClick={() => setMode('reject')} data-danger="true" data-testid={`reject-${change.id}`}>{t('portal.admin.cms.prices.reject')}</Button> : null}
      {can.withdraw ? <Button onClick={() => void done(withdraw.run())} disabled={withdraw.pending} data-testid={`withdraw-${change.id}`}>{t('portal.admin.cms.prices.withdraw')}</Button> : null}
      {can.own && can.approve === false && roles.includes('staff_finance') ? <Text tone="muted" data-testid={`own-${change.id}`}>{t('portal.admin.cms.prices.ownNote')}</Text> : null}
      {mode === 'approve' ? (
        <form className="qx-reason" data-testid={`approve-form-${change.id}`} onSubmit={(e) => { e.preventDefault(); if (stripeOk) void done(approve.run()); }}>
          {can.needsStripeId ? (
            <Field label={t('portal.admin.cms.prices.stripePriceId')} hint={t('portal.admin.cms.prices.stripeHint')}>
              {(id) => <input id={id} name="stripePriceId" value={stripe} placeholder="price_..." onChange={(e) => setStripe(e.target.value)} />}
            </Field>
          ) : <Text tone="muted">{t('portal.admin.cms.prices.wholesaleNoStripe')}</Text>}
          <Field label={t('portal.admin.cms.prices.note')}>{(id) => <input id={id} name="note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />}</Field>
          <Button type="submit" variant="primary" disabled={!stripeOk || approve.pending}>{t('portal.admin.cms.prices.approveConfirm')}</Button>
          <Button onClick={() => setMode(null)}>{t('portal.admin.cms.cancel')}</Button>
        </form>
      ) : null}
      {mode === 'reject' ? (
        <form className="qx-reason" data-testid={`reject-form-${change.id}`} onSubmit={(e) => { e.preventDefault(); if (note.trim().length >= 3) void done(reject.run()); }}>
          <Field label={t('portal.admin.cms.prices.rejectNote')}>{(id) => <textarea id={id} name="note" value={note} minLength={3} maxLength={500} onChange={(e) => setNote(e.target.value)} />}</Field>
          <Button type="submit" data-danger="true" disabled={note.trim().length < 3 || reject.pending}>{t('portal.admin.cms.prices.rejectConfirm')}</Button>
          <Button onClick={() => setMode(null)}>{t('portal.admin.cms.cancel')}</Button>
        </form>
      ) : null}
      <CmsError error={approve.error ?? reject.error ?? withdraw.error} />
    </div>
  );
}
